<template>
  <div class="lesson-room">
    <!-- Lesson Room Header -->
    <div class="lesson-room-header">
      <div class="d-flex justify-content-between align-items-center">
        <div class="lesson-info">
          <h4 class="mb-0" v-if="roomData && roomData.lesson">
            {{ roomData.lesson.name }}
          </h4>
          <p class="text-muted mb-0" v-if="roomData && roomData.webinar">
            {{ formattedDateTime }}
          </p>
        </div>
        <div class="lesson-actions">
          <!-- Teacher: Finish Lesson Button / Completed Badge -->
          <template v-if="roomData && roomData.user_role === 'teacher'">
            <b-badge
              v-if="roomData.webinar && roomData.webinar.status === 'completed'"
              variant="success"
              pill
              class="px-3 py-2"
            >
              <feather-icon icon="CheckCircleIcon" size="16" class="mr-1" />
              Lesson Completed
            </b-badge>
            <b-button
              v-else
              variant="danger"
              size="sm"
              @click="finishLesson"
              :disabled="isLoading"
            >
              <feather-icon icon="CheckCircleIcon" size="16" />
              Finish Lesson
            </b-button>
          </template>

          <!-- Student: Leave Room Button -->
          <b-button
            v-else
            variant="outline-danger"
            size="sm"
            @click="leaveRoom"
            :disabled="isLoading"
          >
            <feather-icon icon="LogOutIcon" size="16" />
            Leave Room
          </b-button>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="text-center py-5">
      <b-spinner variant="primary" />
      <p class="mt-2">Loading lesson room...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="alert alert-danger" role="alert">
      <feather-icon icon="AlertCircleIcon" size="16" />
      {{ error }}
    </div>

    <!-- Completed Lesson Info (for students) -->
    <div v-else-if="roomData && roomData.webinar && roomData.webinar.status === 'completed' && roomData.user_role !== 'teacher'" class="completed-lesson-info">
      <div class="text-center py-5">
        <feather-icon icon="CheckCircleIcon" size="64" class="text-success mb-3" />
        <h4>This lesson has been completed</h4>
        <p class="text-muted">
          The teacher finished this lesson on {{ formatCompletedDate(roomData.webinar.completed_at) }}
        </p>
        <b-button variant="primary" @click="$router.push('/schedule')">
          <feather-icon icon="CalendarIcon" size="16" class="mr-1" />
          Back to Schedule
        </b-button>
      </div>
    </div>

    <!-- Main Content -->
    <div v-else-if="roomData && roomData.webinar" class="lesson-room-content">
      <div class="row h-100">
        <!-- Left Column: Video and Presentation -->
        <div class="col-lg-8 col-md-7">
          <!-- Video Frame -->
          <div class="video-section mb-3" v-if="roomData.room_state && $store.state.user.userData">
            <video-frame
              :jitsi-url="roomData.room_state.jitsi_url"
              :room-name="roomData.room_state.jitsi_room_name"
              :user-name="$store.state.user.userData.name || 'User'"
              :is-active="roomData.room_state.is_active"
              :webinar-id="webinarId"
              :user-role="roomData.user_role"
              :webinar-status="roomData.webinar.status"
              @conference-started="handleConferenceStarted"
              @conference-stopped="handleConferenceStopped"
            />
          </div>

          <!-- Presentation Viewer -->
          <div class="presentation-section">
            <presentation-viewer
              :webinar-id="webinarId"
              :user-role="roomData.user_role"
              @scroll-to-upload="scrollToUploadSection"
            />
          </div>
        </div>

        <!-- Right Column: Chat and Participants -->
        <div class="col-lg-4 col-md-5">
          <!-- Participants List -->
          <div class="participants-section mb-3" v-if="$store.state.user.userData">
            <participants-list
              :participants="participants"
              :current-user-id="$store.state.user.userData.id"
            />
          </div>

          <!-- Chat Panel -->
          <div class="chat-section mb-3">
            <chat-panel
              :webinar-id="webinarId"
              :messages="chatMessages"
              @message-sent="handleMessageSent"
            />
          </div>

          <!-- Quiz Section -->
          <div class="quiz-section">
            <!-- Teacher Quiz Management (teachers only) -->
            <quiz-management
              v-if="roomData.user_role === 'teacher'"
              :webinar-id="webinarId"
            />

            <!-- Student Quiz Taking (students only) -->
            <quiz-taking
              v-else
              :webinar-id="webinarId"
            />
          </div>
        </div>
      </div>

      <!-- Teacher Controls (shown only to teachers) -->
      <div v-if="roomData.user_role === 'teacher'" class="teacher-controls mt-3" ref="teacherControls">
        <div class="teacher-controls-header mb-3">
          <h5 class="mb-1">
            <feather-icon icon="SettingsIcon" size="20" class="mr-2" />
            Teacher Controls
          </h5>
          <p class="text-muted small mb-0">Manage presentations and lesson materials</p>
        </div>
        <div class="row">
          <div class="col-md-8">
            <presentation-upload
              :webinar-id="webinarId"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import moment from 'moment'

// Import lesson room components
import VideoFrame from '@/components/LessonRoom/VideoFrame.vue'
import ChatPanel from '@/components/LessonRoom/ChatPanel.vue'
import PresentationViewer from '@/components/LessonRoom/PresentationViewer.vue'
import PresentationUpload from '@/components/LessonRoom/PresentationUpload.vue'
import ParticipantsList from '@/components/LessonRoom/ParticipantsList.vue'
import QuizManagement from '@/components/LessonRoom/QuizManagement.vue'
import QuizTaking from '@/components/LessonRoom/QuizTaking.vue'

export default {
  name: 'LessonRoom',
  components: {
    VideoFrame,
    ChatPanel,
    PresentationViewer,
    PresentationUpload,
    ParticipantsList,
    QuizManagement,
    QuizTaking
  },
  data() {
    return {
      isLoading: true,
      error: null,
      roomData: {},
      participants: [],
      chatMessages: [],
      echoChannel: null
    }
  },
  computed: {
    webinarId() {
      return this.$route.params.webinarId
    },
    formattedDateTime() {
      if (!this.roomData || !this.roomData.webinar) return ''

      const date = this.roomData.webinar.scheduled_date
      const time = this.roomData.webinar.scheduled_time

      return moment(`${date} ${time}`).format('MMMM Do YYYY, h:mm A')
    }
  },
  async created() {
    await this.initializeRoom()
    this.setupRealTimeConnection()
  },
  beforeDestroy() {
    this.cleanupRealTimeConnection()
    this.leaveRoom()
  },
  methods: {
    async initializeRoom() {
      try {
        this.isLoading = true
        this.error = null

        // Load room data
        await this.loadRoomData()

        // Join the room
        await this.joinRoom()

        // Load chat messages
        await this.loadChatMessages()

        // Load quiz data
        await this.loadQuizData()

      } catch (error) {
        console.error('Error initializing room:', error)
        this.error = error.response?.data?.error || 'Failed to load lesson room'
      } finally {
        this.isLoading = false
      }
    },

    async loadRoomData() {
      // This will call the Vuex action to load room data
      const response = await this.$store.dispatch('lessonRoom/loadRoomData', this.webinarId)
      this.roomData = response
    },

    async joinRoom() {
      // This will call the Vuex action to join the room
      await this.$store.dispatch('lessonRoom/joinRoom', this.webinarId)
    },

    async loadChatMessages() {
      try {
        // Load chat messages from the server
        await this.$store.dispatch('lessonRoom/loadChatMessages', this.webinarId)
        // Update local chat messages from store
        this.chatMessages = this.$store.state.lessonRoom.chatMessages || []
      } catch (error) {
        console.warn('Failed to load chat messages:', error)
        // Don't throw - room should still work without chat history
      }
    },

    async finishLesson() {
      // Show confirmation dialog for teachers
      const confirmed = await this.$bvModal.msgBoxConfirm(
        'Are you sure you want to finish this lesson? The conference will be stopped and the lesson will be marked as completed.',
        {
          title: 'Finish Lesson',
          size: 'md',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: 'Yes, Finish Lesson',
          cancelTitle: 'Cancel',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true
        }
      )

      if (!confirmed) return

      try {
        this.isLoading = true
        // Call the Vuex action to finish the lesson
        await this.$store.dispatch('lessonRoom/finishLesson', this.webinarId)

        this.$bvToast.toast('Lesson has been finished successfully', {
          title: 'Lesson Finished',
          variant: 'success',
          solid: true
        })

        // Navigate back to teacher schedule
        this.$router.push('/schedule/webinar')
      } catch (error) {
        console.error('Error finishing lesson:', error)
        this.$bvToast.toast(error.message || 'Failed to finish lesson', {
          title: 'Error',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.isLoading = false
      }
    },

    async leaveRoom() {
      try {
        // Call the Vuex action to leave the room
        await this.$store.dispatch('lessonRoom/leaveRoom', this.webinarId)

        // Navigate back to schedule based on user role
        const userRole = this.$store.getters['user/role']
        if (userRole === 'teacher') {
          this.$router.push('/schedule/webinar')
        } else {
          this.$router.push('/schedule')
        }
      } catch (error) {
        console.error('Error leaving room:', error)
        // Navigate back anyway based on user role
        const userRole = this.$store.getters['user/role']
        if (userRole === 'teacher') {
          this.$router.push('/schedule/webinar')
        } else {
          this.$router.push('/schedule')
        }
      }
    },

    setupRealTimeConnection() {
      // This will be implemented when Laravel Echo is properly installed
      // For now, we'll create a placeholder structure
      try {
        if (window.Echo) {
          this.echoChannel = window.Echo.private(`lesson-room.${this.webinarId}`)
            .listen('.message.sent', (e) => {
              this.handleNewMessage(e)
            })
            .listen('.participant.updated', (e) => {
              this.handleParticipantUpdate(e)
            })
            .listen('.presentation.changed', (e) => {
              this.handlePresentationUpdate(e)
            })
            .listen('.quiz.started', (e) => {
              this.handleQuizStarted(e)
            })
            .listen('.quiz.ended', (e) => {
              this.handleQuizEnded(e)
            })
            .listen('.quiz.submission', (e) => {
              this.handleQuizSubmission(e)
            })
            .listen('.quiz.results', (e) => {
              this.handleQuizResultsAvailable(e)
            })
        } else {
          console.warn('Laravel Echo not available. Real-time features disabled.')
        }
      } catch (error) {
        console.warn('Failed to setup real-time connection:', error)
      }
    },

    cleanupRealTimeConnection() {
      if (this.echoChannel) {
        this.echoChannel.stopListening('.message.sent')
        this.echoChannel.stopListening('.participant.updated')
        this.echoChannel.stopListening('.presentation.changed')
        this.echoChannel.stopListening('.quiz.started')
        this.echoChannel.stopListening('.quiz.ended')
        this.echoChannel.stopListening('.quiz.submission')
        this.echoChannel.stopListening('.quiz.results')
        this.echoChannel = null
      }
    },

    // Event Handlers
    handleMessageSent(message) {
      this.chatMessages.push(message)
    },

    handleNewMessage(event) {
      this.chatMessages.push(event)
    },

    handleParticipantUpdate(event) {
      const { participant, action } = event

      if (action === 'joined') {
        const existingIndex = this.participants.findIndex(p => p.id === participant.id)
        if (existingIndex === -1) {
          this.participants.push(participant)
        }
      } else if (action === 'left') {
        this.participants = this.participants.filter(p => p.id !== participant.id)
      }
    },

    handlePresentationUpdate(event) {
      this.roomData.current_presentation = event.presentation
      this.roomData.room_state.current_page = event.page
    },

    handlePageChanged(newPage) {
      this.roomData.room_state.current_page = newPage
    },

    handlePresentationUploaded(presentation) {
      this.roomData.presentations.unshift(presentation)
    },

    handlePresentationChanged(data) {
      this.roomData.current_presentation = data.presentation
      this.roomData.room_state.current_page = data.page
    },

    // Quiz-related methods
    async loadQuizData() {
      try {
        // Load active quiz if any
        await this.$store.dispatch('lessonRoom/loadActiveQuiz', this.webinarId)

        // Load test packs for teachers
        if (this.roomData.user_role === 'teacher') {
          await this.$store.dispatch('lessonRoom/loadTestPacks', this.webinarId)
        }
      } catch (error) {
        console.warn('Failed to load quiz data:', error)
        // Don't throw error - room should work without quiz
      }
    },

    // Quiz event handlers
    handleQuizStarted(event) {
      this.$store.dispatch('lessonRoom/handleQuizStarted', event.quiz)

      // Show notification
      this.$bvToast.toast('A new quiz has been started by the teacher', {
        title: 'Quiz Started',
        variant: 'info',
        solid: true
      })
    },

    handleQuizEnded(event) {
      this.$store.dispatch('lessonRoom/handleQuizEnded', {
        quiz: event.quiz,
        stats: event.statistics
      })

      // Show notification
      this.$bvToast.toast('The quiz has been ended by the teacher', {
        title: 'Quiz Ended',
        variant: 'info',
        solid: true
      })
    },

    handleQuizSubmission(event) {
      this.$store.dispatch('lessonRoom/handleQuizSubmission', event.submission)

      // Show notification for teachers
      if (this.roomData.user_role === 'teacher') {
        this.$bvToast.toast(`${event.submission.user_name} submitted their quiz`, {
          title: 'New Submission',
          variant: 'success',
          solid: true
        })
      }
    },

    handleQuizResultsAvailable(event) {
      // Show notification
      this.$bvToast.toast('Quiz results are now available', {
        title: 'Results Available',
        variant: 'success',
        solid: true
      })
    },

    handleConferenceStarted() {
      // Update room state
      if (this.roomData && this.roomData.room_state) {
        this.roomData.room_state.is_active = true
      }
    },

    handleConferenceStopped() {
      // Update room state
      if (this.roomData && this.roomData.room_state) {
        this.roomData.room_state.is_active = false
      }
    },

    formatCompletedDate(timestamp) {
      if (!timestamp) return ''
      return moment(timestamp).format('MMMM Do YYYY, h:mm A')
    },

    scrollToUploadSection() {
      // Scroll to the teacher controls section where the upload component is located
      if (this.$refs.teacherControls) {
        this.$refs.teacherControls.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.lesson-room {
  display: flex;
  flex-direction: column;
  background-color: #f8f8f8;

  .lesson-room-header {
    background: white;
    padding: 1rem;
    border-bottom: 1px solid #e3e3e3;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);

    .lesson-info h4 {
      color: #2c3e50;
      font-weight: 600;
    }
  }

  .lesson-room-content {
    flex: 1;
    padding: 1rem;
    overflow: hidden;

    .row {
      margin: 0;
      height: 100%;
    }

    .video-section {
      background: white;
      border-radius: 8px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.1);
      overflow: hidden;
      min-height: 550px;
      max-height: 600px;
    }

    .presentation-section {
      background: white;
      border-radius: 8px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.1);
      min-height: 600px;
      height: calc(100vh - 650px);
    }

    .presentation-placeholder {
      background: white;
      border-radius: 8px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.1);
      min-height: 300px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .participants-section {
      background: white;
      border-radius: 8px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.1);
      max-height: 200px;
      overflow-y: auto;
    }

    .chat-section {
      background: white;
      border-radius: 8px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.1);
      height: calc(50vh - 200px);
      min-height: 300px;
    }

    .quiz-section {
      background: white;
      border-radius: 8px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.1);
      min-height: 450px;
      max-height: 700px;
      overflow-y: auto;
      padding: 0.5rem;
    }
  }

  .teacher-controls {
    background: white;
    padding: 1.5rem;
    border-top: 2px solid #7367f0;
    box-shadow: 0 -2px 8px rgba(0,0,0,0.15);
    margin-top: 1rem;

    .teacher-controls-header {
      h5 {
        color: #2c3e50;
        font-weight: 600;
        display: flex;
        align-items: center;
      }
    }
  }
}

// Responsive adjustments
@media (max-width: 768px) {
  .lesson-room-content {
    .col-lg-8, .col-lg-4 {
      max-width: 100%;
      flex: 0 0 100%;
    }

    .video-section {
      margin-bottom: 1rem;
    }

    .chat-section {
      height: 300px;
    }
  }
}
</style>